import {
    Autocomplete,
    Card,
    CardContent,
    CardHeader,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch
} from "@mui/material";
import React, {useEffect, useState} from "react";
import Typography from "@mui/material/Typography";
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {routes, useGetDataList} from "../../routes/api";
import {convertRecordTime, CustomTooltip, sleep, stringToHexColor} from "../../util/util";
import {useOutletContext} from "react-router-dom";
import Cookies from "js-cookie";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from '@mui/icons-material/Search';

export default function TrafficCountChart({cameraId, title}) {
    const token = Cookies.get('token');
    const recordRoute = routes.record;

    const [recordType, setRecordType] = useState('TRFFCCNT');

    const [cameraAutoRefresh, setCameraAutoRefresh] = useOutletContext();
    const [granularity, setGranularity] = useState('week');
    const [selectedCamera, setSelectedCamera] = useState('');
    const [submit, setSubmit] = useState(false);

    const {
        data: recordDetail,
        loading: recordLoading,
        statusCode: recordStatus,
        handleCall: recordCall
    } = useGetDataList(token, recordRoute);

    const {data: cameraData, loading: cameraLoading, handleCall: cameraCall} = useGetDataList(token, routes.camera);

    const [allData, setAllData] = useState([]);
    const [objects, setObjects] = useState({});
    const [dataKey, setDataKey] = useState([]);
    const [searching, setSearching] = useState(false);


    const fetchRecordDetail = async (id) => {
        setObjects({});
        setSubmit(true);
        setSearching(true);
        await sleep(1000);
        try {
            await recordCall({
                params: {
                    time_granularity: granularity,
                    r_camera: id,
                    np: 'T',
                    r_type: recordType
                }
            });
        } catch (error) {
            console.error('Error fetching records:', error);
        }
    }

    const fetchCameraData = async () => {
        try {
            await cameraCall({params: {basic: 'T'}});
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }
    useEffect(() => {
        if (!cameraId) {
            fetchCameraData();
        }
    }, []);

    useEffect(() => {
        if (JSON.stringify(recordDetail) !== '{}') {
            const updatedRecords = (recordDetail && recordDetail.length > 0) ? recordDetail.filter((record) => (record.r_timeAdded)).sort((a, b) => new Date(a.r_timeAdded) - new Date(b.r_timeAdded)).map((record) => convertRecordTime(record, granularity)) : [];
            let tempDataKey = [];
            setAllData(updatedRecords.filter(datum => Object.keys(datum.r_detail).includes('counting_results')).map(datum => (
                Object.entries(datum.r_detail.counting_results).reduce((acc, [key, content]) => ({
                    ...acc, ...Object.entries(content).reduce((newContent, [cKey, cValue]) => {
                        if (!tempDataKey.includes(`${key}, ${cKey}`)) {
                            tempDataKey = [...tempDataKey, `${key}, ${cKey}`];
                        }
                        return {
                            ...newContent,
                            [`${key}, ${cKey}`]: cValue
                        };
                    }, {})
                }), {dateKey: datum.dateKey, toolTipTime: datum.toolTipTime})
            )));
            const dataKeyArray = Array.from(new Set(tempDataKey));
            setDataKey(dataKeyArray);
            setObjects(dataKeyArray.reduce((acc, value) => ({
                ...acc,
                [value]: true
            }), {}));
            setSearching(false);
        }
    }, [recordDetail]);

    useEffect(() => {
        if (cameraId) {
            let interval;
            if (cameraAutoRefresh) {
                fetchRecordDetail(cameraId);
                interval = setInterval(() => {
                    fetchRecordDetail(cameraId);
                }, 120000);
            }
            return () => {
                if (interval) clearInterval(interval);
            };
        }
    }, [cameraAutoRefresh]);


    // useEffect(() => {
    //     const savedObjects = JSON?.parse(sessionStorage.getItem('objects'));
    //     if (savedObjects) {
    //         setObjects(savedObjects);
    //     }
    // }, []);

    useEffect(() => {
        if (cameraId) {
            fetchRecordDetail(cameraId);
        }
    }, [granularity]);


    const handleSelectionChange = (event) => {
        const newObject = {
            ...objects,
            [event.target.name]: event.target.checked,
        }
        setObjects(newObject);
        // sessionStorage.setItem('objects', JSON.stringify(newObject));
    };

    const handleGranularityChange = (event) => {
        setGranularity(event.target.value);
    };

    const handleSubmit = () => {
        fetchRecordDetail(selectedCamera);
    }

    const getValue = (options) => {
        let value;
        if (!selectedCamera) {
            value = null;
        } else {
            value = options.find(option => option[0] === selectedCamera) || null;
        }
        return value;
    }

    const handleChange = (newValue) => {
        setSelectedCamera(newValue ? newValue[0] : null);
    }

    return <Card key='chart-card'
                 sx={{borderRadius: '16px', width: '100%', height: '100%', padding: 2, paddingBottom: 0}}>
        <CardHeader
            title={title ?? "Traffic Data"}
            action={<Box sx={{display: 'flex', alignItems: 'center', gap: 1, pr: 1}}>
                {!cameraId && <Autocomplete
                    id='camera'
                    required
                    label='camera'
                    name='camera'
                    value={getValue(cameraData)}
                    filterSelectedOptions
                    isOptionEqualToValue={(option, v) => option[0] === v[0]}
                    getOptionLabel={(option) => option[1] || ''}
                    getOptionKey={(option) => option[0]}
                    options={JSON.stringify(cameraData) === '{}' ? [] : cameraData}
                    onChange={(e, newValue) => {
                        handleChange(newValue);
                    }}
                    loading={cameraLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label='Camera'
                            required
                            size='small'
                            error={!cameraId && !selectedCamera}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {cameraLoading ?
                                                <CircularProgress color="inherit" size={20}/> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }
                            }}
                        />
                    )}
                    sx={{minWidth: {xs: null, md: 150, lg: 200}}}
                />}
                <FormControl variant="outlined" size="small" sx={{ml: 2}}>
                    <InputLabel>Time Range</InputLabel>
                    <Select
                        value={granularity}
                        label="Time Range"
                        onChange={handleGranularityChange}
                        sx={{minWidth: {xs: null, md: 150, lg: 200}}}
                    >
                        <MenuItem value='hour'>1 Hour</MenuItem>
                        <MenuItem value='day'>1 Day</MenuItem>
                        <MenuItem value='week'>1 Week</MenuItem>
                        <MenuItem value='month'>1 Month</MenuItem>
                        <MenuItem value='year'>1 Year</MenuItem>
                    </Select>
                </FormControl>
                {!cameraId &&
                    <IconButton onClick={handleSubmit} size='small' color='primary' disabled={!selectedCamera}>
                        <SearchIcon/>
                    </IconButton>}
            </Box>}
        />
        <CardContent>
            {(allData.length === 0 || searching) ?
                <Grid container spacing={2} alignItems="center" justifyContent="center">
                    <Grid size={12} sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        {searching ?
                            <CircularProgress color="primary" size={40}/> :
                            <Typography variant="button" sx={{fontSize: '1.10rem'}}>
                                {submit && 'No data available'}
                            </Typography>}
                    </Grid>
                </Grid> :
                <Grid container spacing={2} alignItems="center" justifyContent="center">
                    <Grid size={12} sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                        <ResponsiveContainer width="100%" minHeight={500}>
                            <BarChart data={allData} minWidth={450} minHeight={300} barCategoryGap='15%'>
                                <CartesianGrid stroke="#ccc" strokeDasharray="3 3"/>
                                <XAxis dataKey="dateKey" padding={{left: 10, right: 10}}/>
                                <YAxis/>
                                <Tooltip content={<CustomTooltip/>}/>
                                {dataKey.map((dataKey) => (
                                    <Bar key={dataKey}
                                         name={dataKey}
                                         dataKey={dataKey}
                                         maxBarSize={80}
                                         fill={stringToHexColor(dataKey, true)}
                                         hide={!objects[dataKey]}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </Grid>
                    <Grid size={12} sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                        <FormGroup row sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                            {dataKey.map((key) => (
                                <FormControlLabel
                                    control={<Switch/>}
                                    checked={objects[key] || false}
                                    onChange={handleSelectionChange}
                                    name={key}
                                    label={key}
                                    key={key}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: stringToHexColor(key, true),
                                            '&:hover': {
                                                backgroundColor: stringToHexColor(key, true, 0.40),
                                            },
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: stringToHexColor(key, true),
                                        },
                                    }}
                                />
                            ))}
                        </FormGroup>
                    </Grid>
                    <Grid size={12} sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                        <Typography variant="overline" gutterBottom>all timestamps are in user timezone</Typography>
                    </Grid>
                </Grid>}
        </CardContent>
    </Card>
}